import { Api, Environment } from "./api";
import type { ResourceIdentifier } from "./api";
import type { Venue, EventMedia } from "../types/model.types";
import { ApiError, ApiErrors } from "../types/error.types";
import { fetchOneTimeLocation } from "../utils/location";

/** Base class for all API endpoints */
export abstract class Endpoint<T> {
  protected api: Api;
  abstract readonly path: string;
  abstract readonly method: string;
  abstract readonly queryParams: Record<string, string>;
  abstract readonly body?: string;

  constructor(api: Api) {
    this.api = api;
  }

  /** Creates a ResourceIdentifier for this endpoint */
  toResourceIdentifier(): ResourceIdentifier {
    return {
      environment: this.api.config.defaultEnvironment || Environment.PRODUCTION, // Default to production, can be overridden
      path: this.path,
      method: this.method,
      queryParams: this.queryParams,
      body: this.body,
      hash: "",
    };
  }

  /** Base get method that uses the API to make requests */
  async get(): Promise<T> {
    try {
      const resourceIdentifier = this.toResourceIdentifier();
      const response = await this.api.request<T>(resourceIdentifier);
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      } else if (error instanceof Error) {
        throw ApiErrors.Unknown(`SDK Error: ${error.message}`);
      } else {
        throw ApiErrors.Unknown(`Unexpected error: ${String(error)}`);
      }
    }
  }

  /** Helper method to create a new endpoint with additional query parameters */
  protected withQueryParams(
    additionalParams: Record<string, string>,
  ): Endpoint<T> {
    const currentEndpoint = this;
    return new (class extends Endpoint<T> {
      readonly path = currentEndpoint.path;
      readonly method = currentEndpoint.method;
      readonly queryParams = {
        ...currentEndpoint.queryParams,
        ...additionalParams,
      };
      readonly body = currentEndpoint.body;
    })(this.api);
  }
}

/** Class to manage venue-related API requests. */
export class VenuesEndpoint extends Endpoint<Venue[]> {
  readonly path = "venues";
  readonly method = "GET";
  readonly queryParams: Record<string, string> = {};
  readonly body?: string;

  /** Fetches the list of venues */
  async get(): Promise<Venue[]> {
    return super.get();
  }

  /** Provides access to a specific venue endpoint by ID. */
  withId(id: string): VenueEndpoint {
    return new VenueEndpoint(this.api, id);
  }

  /** Filters venues based on their active status. */
  whereIsActive(isActive: boolean = true): VenuesEndpoint {
    return this.withQueryParams({
      active: isActive.toString(),
    }) as unknown as VenuesEndpoint;
  }

  /** Filters venues based on geographical coordinates. */
  at(latitude: number, longitude: number): VenuesEndpoint {
    return this.withQueryParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
    }) as unknown as VenuesEndpoint;
  }

  /**
   * Filters venues based on the current location of the device.
   * Requires location permission from the user.
   */
  async atCurrentLocation(): Promise<VenuesEndpoint> {
    const position = await fetchOneTimeLocation();
    const { latitude, longitude } = position.coords;
    return this.at(latitude, longitude);
  }
}

/** Class to manage API requests related to a specific venue. */
export class VenueEndpoint extends Endpoint<Venue> {
  private readonly id: string;
  readonly path: string;
  readonly method = "GET";
  readonly queryParams: Record<string, string> = {};
  readonly body?: string;

  constructor(api: Api, id: string) {
    super(api);
    this.id = id;
    this.path = `venues/${id}`;
  }

  /** Provides access to the event media endpoint for the venue. */
  get eventMedia(): EventMediaEndpoint {
    return new EventMediaEndpoint(this.api, this.id);
  }

  /** Fetches the venue details */
  async get(): Promise<Venue> {
    return super.get();
  }
}

/** Class to manage API requests related to event media. */
export class EventMediaEndpoint extends Endpoint<EventMedia> {
  readonly path: string;
  readonly method = "GET";
  readonly queryParams: Record<string, string> = {};
  readonly body?: string;

  constructor(api: Api, venueId: string) {
    super(api);
    this.path = `venues/${venueId}/event_media`;
  }

  /** Fetches event media for the venue. */
  async get(): Promise<EventMedia> {
    return super.get();
  }
}
